import AppLayout from '@/Layouts/AppLayout';
import { Card, CardContent } from '@/Components/ui/card';
import { Section, SectionHeader } from '@/Components/Section';
import { FadeIn } from '@/Components/motion';
import { Link } from 'react-router-dom';
import { Separator } from '@/Components/ui/separator';
import { siteConfig } from '@/config/site';

const sections = [
    {
        title: 'Information We Collect',
        content:
            'We collect information you provide directly to us, including your name, email address, phone number, date of birth, and government-issued identification when you create an account or verify your identity. We also collect transaction data, trading activity, and communication records. Additionally, we automatically collect technical information such as your IP address, browser type, device information, cookies, and usage patterns when you interact with our platform.',
    },
    {
        title: 'How We Use Your Information',
        content:
            'We use your personal information to provide, maintain, and improve our services, process transactions and send related notices, verify your identity and prevent fraud, comply with legal and regulatory obligations, communicate with you about products, services, and promotions, personalize your experience on our platform, and conduct analytics to understand how our services are used. We do not sell your personal information to third parties.',
    },
    {
        title: 'Data Sharing',
        content:
            'We may share your personal information with trusted third-party service providers who assist us in operating our platform, processing payments, and delivering services. We may also share information when required by law, to comply with legal processes, to protect our rights and safety, or in connection with a merger, acquisition, or sale of assets. All third-party partners are contractually obligated to protect your data and use it only for the specified purposes.',
    },
    {
        title: 'Data Security',
        content:
            'We implement industry-standard security measures to protect your personal information from unauthorized access, alteration, disclosure, or destruction. These measures include encryption of data in transit and at rest, secure server infrastructure, regular security audits, access controls, and employee training. However, no method of transmission over the internet or electronic storage is 100% secure, and we cannot guarantee absolute security of your data.',
    },
    {
        title: 'Your Rights',
        content:
            'Depending on your jurisdiction, you may have certain rights regarding your personal information under applicable data protection laws, including the General Data Protection Regulation (GDPR). These rights include the right to access, correct, or delete your data, the right to restrict or object to processing, the right to data portability, and the right to withdraw consent at any time. To exercise any of these rights, please contact our privacy team. We will respond to your request within the timeframe required by applicable law.',
    },
    {
        title: 'Data Retention',
        content:
            'We retain your personal information for as long as necessary to fulfill the purposes for which it was collected, including to satisfy any legal, regulatory, accounting, or reporting requirements. The retention period may vary depending on the context of the processing and our legal obligations. When your data is no longer required, we will securely delete or anonymize it. You may request deletion of your data at any time, subject to our legal and regulatory obligations to retain certain records.',
    },
    {
        title: 'Contact',
        content:
            'If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact our privacy team at info@teffunded.com. You may also write to us at our registered business address. We are committed to resolving any privacy-related issues promptly and transparently.',
    },
];

export default function PrivacyPolicy() {
    return (
        <AppLayout title={`Privacy Policy - ${siteConfig.brand.name}`}>
            {/* Hero */}
            <Section className="pt-32 lg:pt-40 pb-20 bg-background">
                <FadeIn>
                    <div className="text-center max-w-4xl mx-auto">
                        <p className="text-sm font-semibold uppercase tracking-widest text-muted-foreground mb-4">
                            Legal
                        </p>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
                            Privacy{' '}
                            <span className="text-gradient">Policy</span>
                        </h1>
                        <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
                            Your privacy matters to us. Learn how we collect,
                            use, and protect your personal information.
                        </p>
                    </div>
                </FadeIn>
            </Section>

            {/* Content */}
            <Section className="py-16 bg-muted/30 border-t border-border">
                <FadeIn>
                    <Card className="max-w-4xl mx-auto rounded-xl border border-border bg-card/80 backdrop-blur-sm">
                        <CardContent className="p-8 md:p-12">
                            <p className="text-sm text-muted-foreground mb-10">
                                Last updated: March 2026
                            </p>

                            <div className="space-y-10">
                                {sections.map((section, index) => (
                                    <div key={section.title}>
                                        <h2 className="text-xl font-semibold text-foreground mb-3">
                                            {index + 1}. {section.title}
                                        </h2>
                                        <p className="text-muted-foreground leading-relaxed">
                                            {section.content}
                                        </p>
                                        {index < sections.length - 1 && (
                                            <Separator className="mt-10" />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </FadeIn>
            </Section>

            {/* Bottom spacer */}
            <Section className="py-16 bg-background border-t border-border">
                <div className="text-center max-w-2xl mx-auto">
                    <p className="text-sm text-muted-foreground">
                        Questions about your privacy?{' '}
                        <Link
                            to="/contact"
                            className="text-primary hover:underline"
                        >
                            Contact us
                        </Link>
                    </p>
                </div>
            </Section>
        </AppLayout>
    );
}
